import {
    faAngleDoubleLeft,
    faAngleDoubleRight,
    faChevronLeft,
    faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';

interface StyledButtonGroupProps {
    isDisabled?: boolean;
    justifyContent?: string;
    children: ReactNode;
}
const StyledButtonGroup = styled.div<StyledButtonGroupProps>`
    display: inline-flex;
    justify-content: ${({ justifyContent }) => justifyContent || 'center'};
    ${({ isDisabled }) =>
        isDisabled &&
        `
    pointer-events: none;
    opacity: 0.6;
  `}

    /* Remove extra border between buttons */
  & > button:not(:last-child) {
        border-right: none;
    }
`;

interface IPaginationButtonProps
    extends Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children' | 'aria-label'> {
    isActive?: boolean;
    isDisabled?: boolean;
    'data-testid'?: string;
}

const PaginationButton = ({
    children,
    'data-testid': dataTestId,
    isActive,
    isDisabled,
    ...rest
}: IPaginationButtonProps) => {
    return (
        <button
            disabled={isDisabled}
            data-testid={dataTestId}
            className={classNames('pagination-ui-btn', {
                'pagination-ui-btn_active': isActive,
            })}
            {...rest}
        >
            {children}
        </button>
    );
};

/* Pagination UI Component */

interface IPaginationUIProps {
    pages: number[];
    prevText?: string;
    nextText?: string;
    isPrevDisabled?: boolean;
    isNextDisabled?: boolean;
    disablePagination?: boolean;
    onPrev: () => void;
    onNext: () => void;
    isActive: (page: number) => boolean;
    onPageChange: (page: number) => void;
    goToFirstPage?: () => void;
    goToLastPage?: () => void;
    isFirstAndLastNavigation?: boolean;
}

export const PaginationUI = ({
    pages,
    prevText,
    nextText,
    isPrevDisabled,
    isNextDisabled,
    disablePagination,
    onPrev,
    onNext,
    isActive,
    onPageChange,
    goToFirstPage,
    goToLastPage,
    isFirstAndLastNavigation = false,
}: IPaginationUIProps) => {
    return (
        <StyledButtonGroup isDisabled={disablePagination} justifyContent="center">
            {isFirstAndLastNavigation && goToFirstPage && (
                <PaginationButton
                    onClick={goToFirstPage}
                    aria-label="First page"
                    isDisabled={isPrevDisabled}
                    data-testid="first-page-button"
                >
                    <FontAwesomeIcon icon={faAngleDoubleLeft} />
                </PaginationButton>
            )}
            <PaginationButton
                onClick={onPrev}
                aria-label="Previous page"
                isDisabled={isPrevDisabled}
                data-testid="previous-button"
            >
                {prevText ? prevText : <FontAwesomeIcon icon={faChevronLeft} />}
            </PaginationButton>
            {pages.map(page => (
                <PaginationButton
                    key={`page_btn_${page}`}
                    isActive={isActive(page)}
                    onClick={() => onPageChange(page)}
                    data-testid={`pagination_button${page}`}
                >
                    {page}
                </PaginationButton>
            ))}
            <PaginationButton
                aria-label="Next page"
                data-testid="next-button"
                onClick={onNext}
                isDisabled={isNextDisabled}
            >
                {nextText ? nextText : <FontAwesomeIcon icon={faChevronRight} />}
            </PaginationButton>
            {isFirstAndLastNavigation && goToLastPage && (
                <PaginationButton
                    onClick={goToLastPage}
                    isDisabled={isNextDisabled}
                    aria-label="Last page"
                    data-testid="last-page-button"
                >
                    <FontAwesomeIcon icon={faAngleDoubleRight} />
                </PaginationButton>
            )}
        </StyledButtonGroup>
    );
};
